<template>
  <div
    v-if="value"
    class="assign-position-dialog-overlay"
    @click.self="handleOverlayClick"
  >
    <div class="assign-position-dialog">
      <!-- Modal Header -->
      <div class="modal-header">
        <div class="header-content">
          <AvatarWithAvailability
            :photoUrl="employeeAvatar"
            :firstName="employeeFirstName"
            :lastName="employeeLastName"
            :size="48"
            :hideAvailabilitySelector="true"
            :showAvailability="true"
          />
        </div>
        <button
          class="close-button"
          @click="handleClose"
          aria-label="Close dialog"
        >
          <Icon name="interface_close" :size="24" />
        </button>
      </div>

      <div class="text-content">
        <h2 class="dialog-title-modal">Assign to occupy position</h2>
        <p class="dialog-subtitle">Pick a position to be occupied by {{ employeeName }}.</p>
        <p v-if="equalVacancy" class="dialog-note">
          Only positions whose vacancy equals the availability of the Employee are available
        </p>
        <p v-else class="dialog-note">Only positions that have vacancy equal or greater than the availability of the Employee are available</p>
      </div>

      <!-- Content -->
      <div class="dialog-content">
        <div class="input-group">
          <PositionsforPrimaryAssignmentPicker
            :key="pickerKey"
            :items="items"
            :isOpen="true"
            :hasMoreData="hasMoreData"
            :isLoadingMore="isLoadingMore"
            :noResults="noResults"
            :customPlaceholder="'Search for Position'"
            :noMatchText="equalVacancy ? noMatchText : ''"
            @item-selected="handlePositionSelected"
            @clear-selection="handlePositionCleared"
            @search="handleSearch"
            @load-more="handleLoadMore"
          />
        </div>

        <div
          v-if="equalVacancy && selectedPosition"
          class="allocation-line"
          data-testid="allocation-line"
        >
          <span class="allocation-line__label">Allocation</span>
          <span class="allocation-line__value">{{ allocationLabel }}</span>
        </div>

        <div class="controls-row mt-6" :class="{ 'controls-row--stacked': equalVacancy }">
          <EffectiveDateToEffectiveTo
            v-if="equalVacancy"
            :effectiveFrom="effectiveDate"
            :effectiveTo="effectiveTo"
            @update:effectiveFrom="handleDateSelected"
            @update:effectiveTo="handleEffectiveToSelected"
          />
          <div v-else class="date-picker-group">
            <label class="input-label-inline">Effective Date</label>
            <DatePicker
              :value="effectiveDate"
              @onApplyDate="handleDateSelected"
              :changeBadgeIndicator="true"
              :modal="true"
            />
          </div>

          <div class="notification-group">
            <SendNotificationInDialog
              :can_send_email="canNotifyEmployee"
              :notify="notifyEmployee"
              :is_employee="true"
              :userLabel="'employee'"
              @update:notify="notifyEmployee = $event"
            />
          </div>
        </div>

        <p v-if="rejectionMessage" class="dialog-rejection" role="alert" data-testid="rejection-message">
          {{ rejectionMessage }}
        </p>
      </div>

      <!-- Modal Actions -->
      <div class="modal-actions">
        <div class="actions-content">
          <div class="actions-right">
            <StandardButton
              type="magenta"
              variant="primary"
              size="normal"
              label="Assign"
              :disabled="!isValid || loading"
              :loaderLeft="loading"
              @click="handleAssign"
              class="assign-button-full-width"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import Icon from "@components/icons/Icon.vue";
import AvatarWithAvailability from "@components/organizations/employees/AvatarWithAvailability.vue";
import PositionsforPrimaryAssignmentPicker from "@components/PositionsforPrimaryAssignmentPicker/PositionsforPrimaryAssignmentPicker.vue";
import DatePicker from "@components/DatePicker/DatePicker.vue";
import SendNotificationInDialog from "@components/SendNotificationInDialog/SendNotificationInDialog.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import EffectiveDateToEffectiveTo from "@components/EffectiveDateToEffectiveTo/EffectiveDateToEffectiveTo.vue";
import { formatFte, noMatchMessage } from "@components/utils/primaryAssignmentPositions";

const props = defineProps({
  value: {
    type: Boolean,
    default: false,
  },
  employeeName: {
    type: String,
    default: "Olivia Rhye",
  },
  employeeFirstName: {
    type: String,
    default: "Olivia",
  },
  employeeLastName: {
    type: String,
    default: "Rhye",
  },
  employeeAvatar: {
    type: String,
    default: null,
  },
  items: {
    type: Array,
    default: () => [],
  },
  hasMoreData: {
    type: Boolean,
    default: false,
  },
  isLoadingMore: {
    type: Boolean,
    default: false,
  },
  noResults: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  canNotifyEmployee: {
    type: Boolean,
    default: true,
  },
  equalVacancy: {
    type: Boolean,
    default: false,
  },
  employeeAvailability: {
    type: Number,
    default: null,
  },
  rejectionMessage: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["input", "assign", "close", "search", "load-more", "date-change"]);

const formatDate = (date) => {
  if (!(date instanceof Date)) return date;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const selectedPosition = ref(null);
const effectiveDate = ref(formatDate(new Date()));
const effectiveTo = ref(null);
const notifyEmployee = ref(true);
const pickerKey = ref(0);

const allocationLabel = computed(() => formatFte(selectedPosition.value?.vacancy));
const noMatchText = computed(() => noMatchMessage(props.employeeAvailability));

const isValid = computed(() => {
  return selectedPosition.value !== null;
});

watch(
  () => props.value,
  (newVal) => {
    if (newVal) {
      // Reset state when dialog opens
      selectedPosition.value = null;
      effectiveDate.value = formatDate(new Date());
      effectiveTo.value = null;
      notifyEmployee.value = true;
    }
  }
);

watch(
  () => props.items,
  (items) => {
    if (!props.equalVacancy || !selectedPosition.value) return;
    if (items.some((item) => item.id === selectedPosition.value.id)) return;
    selectedPosition.value = null;
    pickerKey.value += 1;
  }
);

const handlePositionSelected = (item) => {
  selectedPosition.value = item;
};

const handlePositionCleared = () => {
  selectedPosition.value = null;
};

const handleSearch = (searchTerm) => {
  emit("search", searchTerm);
};

const handleLoadMore = () => {
  emit("load-more");
};

const emitDateChange = () => {
  if (!props.equalVacancy) return;
  emit("date-change", {
    effectiveFrom: effectiveDate.value,
    effectiveTo: effectiveTo.value,
  });
};

const handleDateSelected = (date) => {
  effectiveDate.value = formatDate(date);
  emitDateChange();
};

const handleEffectiveToSelected = (date) => {
  effectiveTo.value = date ? formatDate(date) : null;
  emitDateChange();
};

const handleOverlayClick = () => {
  handleClose();
};

const handleClose = () => {
  emit("input", false);
  emit("close");
};

const handleAssign = () => {
  if (isValid.value) {
    const payload = {
      position: selectedPosition.value,
      effectiveDate: effectiveDate.value,
      notifyEmployee: notifyEmployee.value,
    };
    if (props.equalVacancy) {
      payload.effectiveTo = effectiveTo.value;
      payload.allocation = selectedPosition.value.vacancy;
    }
    emit("assign", payload);
  }
};

// Handle ESC key
const handleKeydown = (event) => {
  if (event.key === "Escape" && props.value) {
    handleClose();
  }
};

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
});
</script>

<style scoped>
.assign-position-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.assign-position-dialog {
  background: white;
  background-image: url("@components/assets/backgrounds/ModalBackgroundPatternsPicker.svg");
  background-repeat: no-repeat;
  background-position: center -6px;
  border-radius: 10px;
  width: 100%;
  max-width: 738px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0px 8px 8px -4px rgba(16, 24, 40, 0.08),
    0px 20px 24px -4px rgba(16, 24, 40, 0.14);
  position: relative;
}

.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  position: relative;
  padding: 24px 24px 0 24px;
  width: 100%;
  z-index: 1;
}

.header-content {
  display: flex;
  gap: 12px;
  flex: 1;
}

.text-content {
  display: flex;
  flex-direction: column;
  padding: 0 24px;
  margin-top: 16px;
  margin-bottom: 24px;
}

.dialog-title-modal {
  font-weight: 600;
  font-size: 18px;
  line-height: 28px;
  color: #101828;
  margin: 0;
}

.dialog-subtitle {
  font-size: 14px;
  color: #475467;
  margin: 4px 0 0 0;
}

.dialog-note {
  font-size: 14px;
  color: #475467;
  margin: 8px 0 0 0;
  line-height: 20px;
}

.close-button {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #98a2b3;
  transition: background-color 0.2s;
}

.dialog-content {
  padding: 0 24px;
  width: 100%;
}

.input-group {
  display: flex;
  flex-direction: column;
}

.input-label {
  font-size: 14px;
  font-weight: 500;
  color: #344054;
  margin-bottom: 6px;
}

.controls-row {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  margin-left: 55px;
}

.date-picker-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.input-label-inline {
  font-size: 14px;
  font-weight: 500;
  color: #344054;
  white-space: nowrap;
}

.notification-group {
  flex: 1;
}

::v-deep .notification-group .send-note-email {
  margin-top: -4px;
}

.mt-6 {
  margin-top: 24px;
}

.allocation-line {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 16px 0 0 55px;
  font-size: 14px;
  line-height: 20px;
}

.allocation-line__label {
  font-weight: 500;
  color: #344054;
}

.allocation-line__value {
  font-weight: 400;
  color: #475467;
}

.controls-row--stacked {
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
}

.dialog-rejection {
  margin: 16px 0 0 55px;
  font-size: 14px;
  line-height: 20px;
  color: #d92d20;
}

.modal-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding-top: 0;
  width: 100%;
  padding-inline: 24px;
}

.actions-content {
  display: flex;
  gap: 12px;
  align-items: center;
  padding-bottom: 24px !important;
  width: 100%;
}

.actions-right {
  display: flex;
  flex: 1 0 0;
  gap: 12px;
  align-items: center;
  justify-content: stretch;
  min-width: 0;
  width: 100%;
}

.assign-button-full-width {
  width: 100% !important;
  flex: 1 0 0;
  cursor: pointer !important;
}

::v-deep .assign-button-full-width button {
  width: 100% !important;
}
</style>
